import { useAuth } from '@/features/auth/use-auth';
import { useTokenExpirationCheck } from '@/features/auth/use-token-expiration-check';
import { MaintenanceScreen } from '@/features/maintenance/maintenance';
import { useMaintenanceCheckQuery } from '@/features/maintenance/maintenance.query';
import { SpinnerFullScreen } from '@/shared/components/spinner';
import { useUiStorePersistent } from '@/shared/store/ui-store';
import { useEffect, useRef } from 'react';

export function AppProtected({
  children,
}: {
  children: React.ReactNode;
}) {
  const { isDarkMode } = useUiStorePersistent();
  const isHandlingKeycloakRedirect = useRef(false);

  const {
    data: isUnderMaintenance,
    isLoading: isMaintenanceCheckLoading,
  } = useMaintenanceCheckQuery();

  const {
    isAuthenticated,
    signInRedirect,
    handleKeycloakRedirect,
    accessTokenExpired,
    refreshTokens,
  } = useAuth();

  useTokenExpirationCheck();

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  useEffect(() => {
    if (isMaintenanceCheckLoading || isUnderMaintenance) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');

    const handleAuth = async () => {
      if (code && !isHandlingKeycloakRedirect.current) {
        isHandlingKeycloakRedirect.current = true;
        await handleKeycloakRedirect();
      } else if (accessTokenExpired) {
        await refreshTokens();
      } else if (!isAuthenticated) {
        await signInRedirect();
      }
    };

    void handleAuth();
  }, [
    isAuthenticated,
    accessTokenExpired,
    signInRedirect,
    handleKeycloakRedirect,
    refreshTokens,
    isUnderMaintenance,
    isMaintenanceCheckLoading,
  ]);

  if (isUnderMaintenance) {
    return <MaintenanceScreen />;
  }

  if (isMaintenanceCheckLoading || !isAuthenticated) {
    return <SpinnerFullScreen />;
  }

  return <>{children}</>;
}
